import { ALL_WIDGET_TYPES, type WidgetType } from './schemas'

/** Options de configuration qu'un widget expose dans le drawer. */
export interface WidgetOptions {
  /** Consomme des données : source, métriques, dimensions, filtres, période. */
  data: boolean
  /** Lit `display.target` (resolveTarget) — sans cible, le widget affiche « À configurer ». */
  target: boolean
  /** Lit `display.limit` (top-N). */
  topN: boolean
}

/**
 * Table exhaustive : `Record<WidgetType, …>` force TypeScript à refuser la
 * compilation si un type de widget est ajouté sans qu'on décide de ses options.
 * Un widget oublié sortirait sinon avec les réglages par défaut, en silence.
 *
 * Les valeurs reflètent ce que les widgets LISENT réellement (voir `widgets/`),
 * pas ce qu'on aimerait leur donner.
 */
const WIDGET_OPTIONS: Record<WidgetType, WidgetOptions> = {
  // Mise en page : aucune donnée.
  divider: { data: false, target: false, topN: false },
  image_block: { data: false, target: false, topN: false },
  rich_text: { data: false, target: false, topN: false },
  date_range_display: { data: false, target: false, topN: false },

  // Indicateurs.
  metric_card: { data: true, target: false, topN: false },
  comparison_metric: { data: true, target: false, topN: false },
  gauge: { data: true, target: true, topN: false },
  progress_bar: { data: true, target: true, topN: false },

  // Graphiques.
  line_chart: { data: true, target: false, topN: false },
  area_chart: { data: true, target: false, topN: false },
  bar_chart: { data: true, target: false, topN: true },
  donut_pie: { data: true, target: false, topN: true },

  // Données.
  table: { data: true, target: false, topN: true },
  top_list: { data: true, target: false, topN: true },
  treemap: { data: true, target: false, topN: true },
  map: { data: true, target: false, topN: true },
  calendar_heatmap: { data: true, target: false, topN: false },
}

export function needsDataSource(type: WidgetType): boolean { return WIDGET_OPTIONS[type].data }
export function needsTarget(type: WidgetType): boolean { return WIDGET_OPTIONS[type].target }
export function hasTopN(type: WidgetType): boolean { return WIDGET_OPTIONS[type].topN }
export function isImageWidget(type: WidgetType): boolean { return type === 'image_block' }
export function isRichTextWidget(type: WidgetType): boolean { return type === 'rich_text' }

/** Tout type déclaré a une entrée dans la table (l'oubli est déjà une erreur de compilation). */
export const ALL_WIDGET_TYPES_COVERED = ALL_WIDGET_TYPES.every((t) => t in WIDGET_OPTIONS)
